import React, { useEffect, useMemo } from "react";
import type { Options } from "react-joyride";

import {
  UserGuidedTour,
  type UserGuidedTourStepDefinition,
} from "components/tours/UserGuidedTour";
import { useResponsiveLayout } from "hooks/useResponsiveLayout";
import { setGuidedTourPromptBlocker } from "utils/foregroundPrompt";

const DASHBOARD_HOME_OVERVIEW_TOUR_KEY = "dashboard-home-overview-v1";
const DASHBOARD_HOME_LOADING_PROMPT_BLOCKER_KEY = "dashboard-home-overview-loading";

const STEP_DEFINITIONS: UserGuidedTourStepDefinition[] = [
  {
    selector: '[data-tour-id="dashboard-continue-course"]',
    title: "Continue Your Training",
    content:
      "This card shows your main training path and where to continue your next core lesson or module.",
    placement: "bottom",
  },
  {
    selector: '[data-tour-id="dashboard-quick-wins"]',
    title: "Quick Wins",
    content:
      "These are shorter optional lessons and reflections you can complete in a single sitting.",
    placement: "bottom",
  },
  {
    selector: '[data-tour-id="dashboard-daily-check-in"]',
    title: "Daily Check-in",
    content:
      "Update due goals, complete your Daily Pulse, and revisit recent days from this card.",
    placement: "top",
  },
];

const ANALYTICS_METADATA = {
  page_name: "home_dashboard",
};

const JOYRIDE_OPTIONS: Partial<Options> = {
  scrollOffset: 80,
  skipScroll: false,
};

type DashboardOverviewTourProps = {
  isActive: boolean;
  launchToken: number;
};

export const DashboardOverviewTour: React.FC<DashboardOverviewTourProps> = ({
  isActive,
  launchToken,
}) => {
  const { isMobile } = useResponsiveLayout();
  const stepDefinitions = useMemo(
    () =>
      STEP_DEFINITIONS.map((step) => ({
        ...step,
        placement: isMobile ? ("center" as const) : step.placement,
      })),
    [isMobile],
  );

  useEffect(() => {
    setGuidedTourPromptBlocker(DASHBOARD_HOME_LOADING_PROMPT_BLOCKER_KEY, !isActive);
    return () => setGuidedTourPromptBlocker(DASHBOARD_HOME_LOADING_PROMPT_BLOCKER_KEY, false);
  }, [isActive]);

  return (
    <UserGuidedTour
      analyticsMetadata={ANALYTICS_METADATA}
      analyticsPathname="/home"
      analyticsScreenName="home_dashboard"
      analyticsSurface="home"
      isActive={isActive}
      joyrideOptions={JOYRIDE_OPTIONS}
      launchToken={launchToken}
      scrollToFirstStep={!isMobile}
      stepDefinitions={stepDefinitions}
      tourKey={DASHBOARD_HOME_OVERVIEW_TOUR_KEY}
    />
  );
};
